<template>
  <div class="mtp">
    <!-- ═══════════ Galerie: eine Karte je Template ═══════════ -->
    <template v-if="view === 'grid'">
      <div class="mtp-toolbar">
        <div>
          <div class="mtp-toolbar__title">Mail-Templates</div>
          <div class="mtp-toolbar__sub">
            Kundenmails in {{ langItems.length }} Sprachen — Änderungen gelten sofort für alle künftigen Mails.
          </div>
        </div>
        <v-spacer />
        <v-select
          v-model="selectedStoreKey"
          :items="storeItems"
          outlined dense hide-details
          class="mtp-select mtp-select--store"
          menu-props="offsetY"
          prepend-inner-icon="mdi-storefront-outline"
          title="Store-Sicht für Vorschau und Testmail"
        />
        <v-switch v-model="testViaDispatcher" dense hide-details class="ml-4 mtp-viaswitch" label="Testmails über Dispatcher" title="Testmails der Templates über den neuen Dispatcher statt über die Extension senden" />
      </div>

      <!-- ═══════════ Mail-Versand (Dispatcher): eigener Ersatz der Extension, Collection mailDispatcher ═══════════ -->
      <div class="mtp-switchcard mtp-dispatch" :class="{ 'mtp-switchcard--on': disp.enabled }">
        <div class="mtp-switchcard__head">
          <v-icon small :color="disp.enabled ? '#2e7d32' : '#9e9e9e'">mdi-send-check-outline</v-icon>
          <div>
            <div class="mtp-switchcard__title">Mail-Versand (Dispatcher)</div>
            <div class="mtp-switchcard__sub">Eigener Versand über SMTP (Google Workspace) mit Wiederholung, Bounce-Erkennung und Resend-Rückfall. Hört nur auf <code>mailDispatcher</code>; die alte Extension bleibt auf <code>mail</code>, bis die Erzeuger umgestellt sind.</div>
          </div>
          <v-spacer />
          <v-switch v-model="disp.enabled" inset dense hide-details color="#2e7d32" :label="disp.enabled ? 'Dispatcher aktiv' : 'Dispatcher aus'" :loading="dispSaving" @change="saveDisp" />
        </div>

        <div class="mtp-dispatch__stats">
          <div v-for="k in statKeys" :key="k.key" class="mtp-stat" :class="k.cls">
            <div class="mtp-stat__val">{{ statToday[k.key] || 0 }}<span class="mtp-stat__week">/ {{ statWeek[k.key] || 0 }}</span></div>
            <div class="mtp-stat__label">{{ k.label }}</div>
          </div>
          <div class="mtp-stat__hint">heute / 7 Tage</div>
        </div>

        <div class="mtp-switchcard__row">
          <v-text-field v-model="disp.redirectAllTo" outlined dense hide-details label="Test: alle Mails umleiten an (leer = echter Empfänger)" class="mtp-dispatch__redirect" prepend-inner-icon="mdi-redo-variant" @change="saveDisp" />
          <v-btn text small color="#7b5cd6" class="mtp-btn" :loading="dispBusy === 'test'" @click="dispTestSend"><v-icon small left>mdi-email-fast-outline</v-icon>Testmail an mich</v-btn>
          <v-btn text small color="#7b5cd6" class="mtp-btn" :loading="dispBusy === 'fallback'" title="Testmail per SMTP, dann simulierter weicher Bounce → Zweitversuch über Resend (Store-Auswahl oben)" @click="dispTestFallback"><v-icon small left>mdi-email-sync-outline</v-icon>Rückfall testen</v-btn>
          <v-btn text small color="#7b5cd6" class="mtp-btn" :loading="dispBusy === 'sweep'" @click="dispAction('sweepNow')"><v-icon small left>mdi-broom</v-icon>Nachlauf jetzt</v-btn>
          <v-btn text small class="mtp-btn" @click="dispShowStores = !dispShowStores"><v-icon small left>mdi-storefront-outline</v-icon>Absender je Store</v-btn>
          <v-btn text small class="mtp-btn" @click="dispShowLog = !dispShowLog"><v-icon small left>mdi-format-list-bulleted</v-icon>Letzte Mails ({{ dispRecent.length }})</v-btn>
        </div>

        <div class="mtp-dispatch__mailbox">
          <v-icon small :color="disp.bounceMailbox ? '#2e7d32' : '#9e9e9e'">{{ disp.bounceMailbox ? 'mdi-inbox-arrow-down' : 'mdi-inbox-arrow-down-outline' }}</v-icon>
          <template v-if="disp.bounceMailbox">
            <span>Bounce-Postfach <b>{{ disp.bounceMailbox.email }}</b> verbunden (nur Lesen)</span>
            <span class="mtp-badge" v-if="disp.bounceMailbox.lastPollAt" :title="new Date(disp.bounceMailbox.lastPollAt).toLocaleString('de-DE')">zuletzt geprüft {{ relTime(disp.bounceMailbox.lastPollAt) }}</span>
            <span class="mtp-badge mtp-badge--warn" v-if="disp.bounceMailbox.lastError" :title="disp.bounceMailbox.lastError">Fehler beim Abruf</span>
            <v-spacer />
            <v-btn text small color="#7b5cd6" class="mtp-btn" :loading="dispBusy === 'poll'" @click="dispAction('pollNow')"><v-icon small left>mdi-refresh</v-icon>Jetzt prüfen</v-btn>
            <v-btn text small class="mtp-btn" @click="dispAction('disconnectMailbox')">Trennen</v-btn>
          </template>
          <template v-else>
            <span>Kein Bounce-Postfach verbunden — Zustellfehler (geblockt, Adresse unbekannt) bleiben unsichtbar.</span>
            <v-spacer />
            <v-btn depressed small color="#7b5cd6" dark class="mtp-btn" :loading="dispBusy === 'oauth'" @click="dispConnectMailbox"><v-icon small left>mdi-google</v-icon>Postfach verbinden</v-btn>
          </template>
        </div>
        <div v-if="dispCallbackUrl" class="mtp-dispatch__callback">Rückruf-URL für den Google-OAuth-Client (einmalig eintragen): <code>{{ dispCallbackUrl }}</code></div>

        <div v-if="dispShowStores" class="mtp-dispatch__stores">
          <div class="mtp-dispatch__storesHead">Absender je Store — leer = Standardwert aus dem Code. Aliase müssen im Google-Postfach als „Senden als“ eingerichtet sein; Resend-Absender nur für verifizierte Domains (mailer.…).</div>
          <div v-for="row in storeRows" :key="row.key" class="mtp-dispatch__storeRow">
            <div class="mtp-dispatch__storeName"><b>{{ row.key }}</b><span>{{ row.domain }}</span></div>
            <!-- leer = Code-Standard; der steht als Hinweis unter dem Feld (Vuetify zeigt placeholder nur im Fokus) -->
            <v-text-field v-model="row.teamFrom" outlined dense persistent-hint :hint="row.teamFrom ? '' : 'Standard: ' + row.defaults.teamFrom" label="Team-Absender" @change="saveStores" />
            <v-text-field v-model="row.teamReplyTo" outlined dense persistent-hint :hint="row.teamReplyTo ? '' : 'Standard: ' + row.defaults.teamReplyTo" label="Team Reply-To" @change="saveStores" />
            <v-text-field v-model="row.noreplyFrom" outlined dense persistent-hint :hint="row.noreplyFrom ? '' : 'Standard: ' + row.defaults.noreplyFrom" label="Automatik-Absender" @change="saveStores" />
            <v-text-field v-model="row.noreplyReplyTo" outlined dense persistent-hint :hint="row.noreplyReplyTo ? '' : 'Standard: ' + row.defaults.noreplyReplyTo" label="Automatik Reply-To" @change="saveStores" />
            <v-text-field v-model="row.resendFrom" outlined dense persistent-hint :hint="row.resendFrom ? '' : 'kein Rückfall (Domain bei Resend nicht verifiziert)'" label="Resend-Rückfall (From)" @change="saveStores" />
          </div>
        </div>

        <div v-if="dispShowLog" class="mtp-dispatch__log">
          <div v-for="m in dispRecent" :key="m.id" class="mtp-dispatch__logRow" :class="'mtp-dispatch__logRow--' + stateClass(m)">
            <span class="mtp-dispatch__logState" :title="(m.delivery && (m.delivery.error || (m.delivery.bounce && m.delivery.bounce.diagnostic))) || ''">{{ stateLabel(m) }}</span>
            <span class="mtp-dispatch__logWhen">{{ relTime(m.createdAt) }}</span>
            <span class="mtp-dispatch__logTo">{{ maskMail(m.to) }}</span>
            <span class="mtp-dispatch__logSubject">{{ (m.message && m.message.subject) || '' }}</span>
            <span class="mtp-dispatch__logStore">{{ m.shopId ? m.shopId + '_' + m.countryCode : (m.from || '').replace(/<.*/, '') }}</span>
            <v-btn v-if="['ERROR', 'FAILED', 'BOUNCED'].includes(m.delivery && m.delivery.state)" x-small text color="#7b5cd6" @click="dispAction('retry', { id: m.id })">erneut</v-btn>
          </div>
          <div v-if="!dispRecent.length" class="mtp-dispatch__empty">Noch keine Mails über den Dispatcher.</div>
        </div>
      </div>

      <!-- Adress-Check-Mails: Schalter + Zeiten (settings/addressCheck) — Versand erst nach Maltes Freigabe -->
      <div class="mtp-switchcard" :class="{ 'mtp-switchcard--on': addrMail.mail }">
        <div class="mtp-switchcard__head">
          <v-icon small :color="addrMail.mail ? '#2e7d32' : '#9e9e9e'">mdi-map-marker-alert-outline</v-icon>
          <div>
            <div class="mtp-switchcard__title">Adress-Check-Mails</div>
            <div class="mtp-switchcard__sub">Nur bei roter Google-Adressprüfung, frühestens {{ addrMail.mailDelayHours || 1 }} h nach Bestellung; Erinnerung nach {{ addrMail.mailReminderHours || 48 }} h ohne Rückmeldung. Statusseiten-Box ist unabhängig davon aktiv.</div>
          </div>
          <v-spacer />
          <v-switch v-model="addrMail.mail" inset dense hide-details color="#2e7d32" :label="addrMail.mail ? 'Versand aktiv' : 'Versand aus'" :loading="addrMailSaving" @change="saveAddrMail" />
        </div>
        <div class="mtp-switchcard__row">
          <v-text-field v-model.number="addrMail.mailDelayHours" type="number" min="0" step="0.5" outlined dense hide-details label="Erste Mail nach (h)" class="mtp-switchcard__num" @change="saveAddrMail" />
          <v-text-field v-model.number="addrMail.mailReminderHours" type="number" min="1" outlined dense hide-details label="Erinnerung nach (h)" class="mtp-switchcard__num" @change="saveAddrMail" />
          <v-text-field v-model.number="addrMail.mailMaxAgeDays" type="number" min="1" outlined dense hide-details label="Max. Alter (Tage)" class="mtp-switchcard__num" @change="saveAddrMail" />
          <span v-if="addrMail.mailSince" class="mtp-badge" :title="new Date(addrMail.mailSince).toLocaleString('de-DE')">Bestellungen ab {{ shortDate(new Date(addrMail.mailSince).toISOString()) }}</span>
          <v-spacer />
          <v-btn text small color="#7b5cd6" class="mtp-btn" :loading="addrMailDryRunning" @click="addrMailDryRun"><v-icon small left>mdi-magnify</v-icon>Wer bekäme jetzt eine Mail?</v-btn>
        </div>
        <div v-if="addrMailDry" class="mtp-switchcard__result">
          <template v-if="addrMailDry.candidates.length">
            <b>{{ addrMailDry.candidates.length }}</b> Kandidat{{ addrMailDry.candidates.length === 1 ? '' : 'en' }}:
            <span v-for="c in addrMailDry.candidates" :key="c.orderName" class="mtp-badge" :class="c.kind === 'reminder' ? 'mtp-badge--warn' : ''">{{ c.orderName }} · {{ c.kind === 'reminder' ? 'Erinnerung' : 'erste Mail' }} · {{ c.locale || '?' }}</span>
          </template>
          <template v-else>Aktuell würde niemand eine Mail bekommen.</template>
          <span class="mtp-switchcard__skipped">({{ addrMailDry.scanned }} rote geprüft — übersprungen: {{ Object.entries(addrMailDry.skipped || {}).map(([k, v]) => `${k} ${v}`).join(', ') || '—' }})</span>
        </div>
      </div>

      <div v-if="!loading && !templateList.length" class="mtp-empty">
        Noch keine Templates in <code>transactionalMailTemplates</code>. Sie werden beim ersten Versand aus den Standards gesät.
      </div>

      <div class="mtp-gallery">
        <div v-for="tpl in templateList" :key="tpl.id" class="mtp-tplcard" :class="{ 'mtp-tplcard--incomplete': tpl.missingLangs > 0 }">
          <div class="mtp-tplcard__preview" @click="openEditor(tpl.id)">
            <iframe class="mtp-tplcard__frame" :srcdoc="cardHtml(tpl)" sandbox="" scrolling="no" tabindex="-1" :title="tpl.name" />
            <div class="mtp-tplcard__hover">
              <v-icon color="white">mdi-pencil</v-icon>
            </div>
          </div>
          <div class="mtp-tplcard__meta">
            <div class="mtp-tplcard__icon">
              <v-icon small>{{ tpl.icon }}</v-icon>
            </div>
            <div class="mtp-tplcard__text">
              <div class="mtp-tplcard__name">{{ tpl.name }}</div>
              <div class="mtp-tplcard__desc">{{ tpl.description }}</div>
              <div class="mtp-tplcard__badges">
                <span v-if="tpl.missingLangs === 0" class="mtp-badge mtp-badge--ok">{{ langItems.length }} Sprachen vollständig</span>
                <span v-else class="mtp-badge mtp-badge--warn">{{ tpl.missingLangs }} {{ tpl.missingLangs === 1 ? "Sprache" : "Sprachen" }} unvollständig</span>
                <span v-if="tpl.updatedAt" class="mtp-badge" :title="tpl.updatedAt">bearbeitet {{ shortDate(tpl.updatedAt) }}</span>
                <span v-else class="mtp-badge">Standardtexte</span>
              </div>
            </div>
          </div>
          <div class="mtp-tplcard__actions">
            <v-btn text small color="#7b5cd6" class="mtp-btn" :loading="sendingTestId === tpl.id" @click="sendTestMail(tpl.id)">
              <v-icon small left>mdi-email-check-outline</v-icon>
              Testmail
            </v-btn>
            <v-spacer />
            <v-btn text small color="#7b5cd6" class="mtp-btn" @click="openEditor(tpl.id)">
              Bearbeiten
              <v-icon small right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </template>

    <!-- ═══════════ Editor: Texte je Sprache + Vorschau ═══════════ -->
    <template v-else-if="template">
      <div class="mtp-toolbar">
        <v-btn icon class="mtp-back" title="Zurück zur Übersicht" @click="backToGrid">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="mtp-toolbar__icon">
          <v-icon small>{{ meta(selectedTemplateId).icon }}</v-icon>
        </div>
        <div>
          <div class="mtp-toolbar__title">{{ meta(selectedTemplateId).name }}</div>
          <div class="mtp-toolbar__sub">{{ meta(selectedTemplateId).description }}</div>
        </div>
        <v-spacer />
        <v-select
          v-model="selectedStoreKey"
          :items="storeItems"
          outlined dense hide-details
          class="mtp-select mtp-select--store"
          menu-props="offsetY"
          prepend-inner-icon="mdi-storefront-outline"
        />
        <v-text-field
          v-model="testOrderRef"
          outlined dense hide-details clearable
          class="mtp-select mtp-select--order"
          placeholder="#91798 (optional)"
          prepend-inner-icon="mdi-package-variant-closed"
          title="Testmail mit echter Bestellung: Sprache, Store, Kunde und Bilder kommen dann aus der Order"
        />
        <v-btn
          outlined rounded color="#7b5cd6"
          class="mtp-btn"
          :loading="sendingTestId === selectedTemplateId"
          @click="sendTestMail(selectedTemplateId)"
        >
          <v-icon small left>mdi-email-check-outline</v-icon>
          Testmail an mich
        </v-btn>
        <v-btn
          rounded depressed dark
          color="#7b5cd6"
          class="mtp-btn"
          :disabled="!isDirty"
          :loading="saving"
          @click="saveLang"
        >
          <v-icon small left>mdi-content-save-outline</v-icon>
          Speichern
        </v-btn>
      </div>

      <!-- Sprach-Pills mit Vollständigkeits-Ampel -->
      <div class="mtp-langs">
        <button
          v-for="l in langItems"
          :key="l.value"
          class="mtp-lang"
          :class="{ 'mtp-lang--active': selectedLang === l.value }"
          @click="switchLang(l.value)"
        >
          <span
            class="mtp-lang__dot"
            :class="completeness[l.value] === 0 ? 'mtp-lang__dot--ok' : 'mtp-lang__dot--missing'"
          />
          {{ l.text }}
          <span v-if="completeness[l.value] > 0" class="mtp-lang__count">{{ completeness[l.value] }}</span>
        </button>
      </div>

      <div class="mtp-grid">
        <v-card class="mtp-card">
          <div class="mtp-card__head">
            <span class="mtp-card__title">
              <v-icon small class="mtp-card__icon">mdi-translate</v-icon>
              Texte — {{ langLabel(selectedLang) }}
            </span>
            <span v-if="selectedLang !== 'de'" class="mtp-muted">Referenz darunter: Deutsch</span>
          </div>

          <div v-for="key in textKeys" :key="key" class="mtp-key">
            <div class="mtp-key__label">
              <code>{{ key }}</code>
              <span v-if="placeholderMismatch(key)" class="mtp-warn" title="Platzhalter weichen vom deutschen Original ab">
                <v-icon x-small color="#b26a00">mdi-code-braces</v-icon> Platzhalter prüfen
              </span>
            </div>
            <v-textarea
              v-model="draft[key]"
              :rows="rowsFor(key)"
              auto-grow dense outlined hide-details
              class="mtp-key__input"
              :class="{ 'mtp-key__input--empty': !String(draft[key] || '').trim() }"
            />
            <div v-if="selectedLang !== 'de'" class="mtp-key__reference">
              {{ deTexts[key] }}
            </div>
          </div>
        </v-card>

        <v-card class="mtp-card mtp-card--preview">
          <div class="mtp-card__head">
            <span class="mtp-card__title">
              <v-icon small class="mtp-card__icon">mdi-eye-outline</v-icon>
              Vorschau
            </span>
            <span class="mtp-subject" :title="previewSubject">{{ previewSubject }}</span>
          </div>
          <iframe
            class="mtp-preview"
            :srcdoc="previewHtml"
            sandbox=""
            title="Mail-Vorschau"
          />
        </v-card>
      </div>
    </template>

    <v-overlay :value="loading" opacity="0.15">
      <v-progress-circular indeterminate color="#7b5cd6" />
    </v-overlay>
  </div>
</template>

<script>
import { collection, doc, onSnapshot, updateDoc, setDoc, query, orderBy, limit } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions as fbFunctions } from "../../../../fb";
import { EventBus, events } from "../../../../event-bus";

// Anzeige-Namen + Auslöser der Templates (IDs = transactionalMailTemplates-Doc-IDs). Die Reihenfolge hier ist
// die Reihenfolge der Karten; unbekannte Docs (z. B. neu gesät) hängen hinten an und nutzen name/description
// aus dem Doc, sonst die ID.
const TEMPLATE_META = {
  updates_artwork: { name: "Live-Vorschau / Refinement", description: "Kunde bekommt die Vorschau seines Kunstwerks zur Bewertung", icon: "mdi-image-edit-outline" },
  send_results_link: { name: "Ergebnis — Customizer", description: "Fertiges Kunstwerk aus dem Customizer, Link zum Ergebnis", icon: "mdi-check-decagram-outline" },
  send_results_digital: { name: "Ergebnis — Digital", description: "Digitale Bestellung fertig, Link zu den Downloads", icon: "mdi-download-outline" },
  send_results_link_preview: { name: "Gratis-Vorschau + Coupon", description: "Kostenlose Vorschau mit Rabattcode für die Bestellung", icon: "mdi-ticket-percent-outline" },
  action_invalid_image: { name: "Upload-Alternativen (Rahmen)", description: "Foto ungeeignet: Team-Text mit Link für alternative Uploads", icon: "mdi-image-off-outline" },
  order_edit_link: { name: "Änderungslink", description: "Kunde darf Bild, Text, Hintergrund, Größe oder Extras seiner Bestellung ändern", icon: "mdi-link-variant" },
  order_edit_gift_card: { name: "Gutschrift nach Änderung", description: "Gutschein-Code, wenn eine Bestelländerung günstiger wurde (2 Jahre gültig)", icon: "mdi-wallet-giftcard" },
  address_check: { name: "Adress-Check", description: "Google konnte die Lieferadresse nicht zuordnen: Hinweis mit Vorschlag und Link zur Statusseite (1 h nach Bestellung)", icon: "mdi-map-marker-alert-outline" },
  address_check_reminder: { name: "Adress-Check — Erinnerung", description: "Erinnerung nach 48 h ohne Rückmeldung des Kunden", icon: "mdi-bell-alert-outline" },
};
const TEMPLATE_ORDER = Object.keys(TEMPLATE_META);

const LANGS = [
  { value: "de", text: "DE" },
  { value: "en", text: "EN" },
  { value: "fr", text: "FR" },
  { value: "es", text: "ES" },
  { value: "it", text: "IT" },
  { value: "nl", text: "NL" },
  { value: "pl", text: "PL" },
  { value: "pt", text: "PT" },
  { value: "da", text: "DA" },
  { value: "sv", text: "SV" },
  { value: "cs", text: "CS" },
  { value: "sl", text: "SL" },
  { value: "sk", text: "SK" },
];

const LANG_NAMES = {
  de: "Deutsch", en: "Englisch", fr: "Französisch", es: "Spanisch", it: "Italienisch",
  nl: "Niederländisch", pl: "Polnisch", pt: "Portugiesisch", da: "Dänisch",
  sv: "Schwedisch", cs: "Tschechisch", sl: "Slowenisch", sk: "Slowakisch",
};

// Store-Sicht für Vorschau/Testversand — Branding-Spiegel von
// shopBasedEnvVariables in functions/utils.js (DOMAIN/LOGO_URL/EMAIL_REPLY_TO).
// Bei Änderungen dort BEIDE Stellen pflegen.
const STORES = [
  { key: "AQ_DE", shopId: "AQ", countryCode: "DE", text: "Artquarell.com", defaultLang: "de",
    brandName: "Artquarell.com", supportEmail: "du@artquarell.com",
    logoUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600" },
  { key: "AQ_ES", shopId: "AQ", countryCode: "ES", text: "Artcuarela.es", defaultLang: "es",
    brandName: "Artcuarela.es", supportEmail: "lisa@artcuarela.es",
    logoUrl: "https://artcuarela.es/cdn/shop/files/logo_right_360x.png?v=1669124511" },
  { key: "AQ_FR", shopId: "AQ", countryCode: "FR", text: "Artquarelle.fr", defaultLang: "fr",
    brandName: "Artquarelle.fr", supportEmail: "support@artquarelle.fr",
    logoUrl: "https://artquarelle.fr/cdn/shop/files/logo_FR-min.png?v=1724933265&width=500" },
  { key: "AQ_PL", shopId: "AQ", countryCode: "PL", text: "Artkwarela.pl", defaultLang: "pl",
    brandName: "Artkwarela.pl", supportEmail: "ty@artkwarela.pl", logoUrl: "" },
  { key: "TM_DE", shopId: "TM", countryCode: "DE", text: "TierBeiMir.com", defaultLang: "de",
    brandName: "TierBeiMir.com", supportEmail: "lisa@tierbeimir.com", logoUrl: "" },
  { key: "OH_US", shopId: "OH", countryCode: "US", text: "OhMy.art", defaultLang: "en",
    brandName: "OhMy.art", supportEmail: "support@ohmy.art",
    logoUrl: "https://cdn.shopify.com/s/files/1/0895/9112/0219/files/ohmyart_loogo_large_fd739e6a-be5b-4e4a-9181-069174ebf16e_430x.png" },
];

// Beispieldaten für die Vorschau — Spiegel von SAMPLE_DATA in
// functions/artquarellMain/TransactionalMail/transactionalMail.js
const SAMPLE_DATA = {
  customerName: "Malte",
  orderName: "#TEST",
  resultUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  resultThumbnailUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  mockupUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  downloadsUrl: "https://status.artquarell.com",
  rateUpUrl: "https://status.artquarell.com",
  rateDownUrl: "https://status.artquarell.com",
  supportMessageUrl: "https://status.artquarell.com",
  uploadAlternativesUrl: "https://status.artquarell.com",
  noAlternativesUrl: "https://status.artquarell.com",
  couponCode: "TESTCODE5",
  editUrl: "https://artquarell.com/products/beispiel?orderEdit=TEST",
  expiresHours: "96",
  subject: "Test-Betreff (Team-Text)",
  message: "<p>Test-Nachricht — hier steht im Ernstfall der im Dialog verfasste Team-Text.</p>",
  // Gutschrift nach Bestelländerung
  giftCardCode: "ABCD EFGH IJKL MNOP",
  amount: "20,00 €",
  expiresOn: "20.10.2028",
  summary: "Leinwand 75×50 cm → Poster 30×20 cm",
  shopUrl: "https://artquarell.com",
  brandName: "Artquarell.com",
  supportEmail: "du@artquarell.com",
  logoUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  noLogo: false,
};

// Mini-Renderer — 1:1-Spiegel von AbandonedCheckout/templateRenderer.js
const renderConditionals = (html, data) =>
  html.replace(/\{\{#if\s+([a-zA-Z0-9_.]+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (_, key, body) => (data[key] ? body : ""));
const renderData = (html, data) =>
  html.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (_, key) => {
    const v = data[key];
    return v === undefined || v === null ? "" : String(v);
  });
const renderTexts = (html, texts) =>
  html.replace(/\{\{t\.([a-zA-Z0-9_]+)\}\}/g, (_, key) => {
    const v = texts?.[key];
    return v === undefined || v === null ? "" : String(v);
  });

const extractPlaceholders = (s) => (String(s || "").match(/\{\{[a-zA-Z0-9_]+\}\}/g) || []).sort().join(",");

const renderMail = (template, texts, store, lang) => {
  if (!template?.html) return { html: "", subject: "" };
  const merged = {
    ...texts,
    ...SAMPLE_DATA,
    brandName: store.brandName,
    supportEmail: store.supportEmail,
    logoUrl: store.logoUrl,
    noLogo: !store.logoUrl,
    langCode: lang,
  };
  let html = renderTexts(String(template.html), texts);
  html = renderConditionals(html, merged);
  html = renderData(html, merged);
  const subject = renderData(renderConditionals(String(texts.subject || ""), merged), merged);
  return { html, subject };
};

export default {
  name: "MailTemplatesPanel",
  data() {
    return {
      // Mail-Dispatcher (settings/mailDispatcher, mailStats, mailDispatcher)
      disp: { enabled: false, redirectAllTo: "", stores: {}, bounceMailbox: null },
      dispDefaults: {},
      dispSaving: false,
      dispBusy: "",
      dispShowStores: false,
      dispShowLog: false,
      dispCallbackUrl: "",
      dispRecent: [],
      dispStats: {},
      testViaDispatcher: false,
      unsubDisp: null,
      unsubDispRecent: null,
      unsubDispStats: null,
      // Adress-Check-Mails (settings/addressCheck)
      addrMail: { mail: false, mailSince: null, mailDelayHours: 1, mailReminderHours: 48, mailMaxAgeDays: 14 },
      addrMailSaving: false,
      addrMailDryRunning: false,
      addrMailDry: null,
      unsubAddrMail: null,
      templates: {},
      view: "grid",
      selectedTemplateId: null,
      selectedStoreKey: "AQ_DE",
      selectedLang: "de",
      testOrderRef: "",
      draft: {},
      loading: true,
      saving: false,
      sendingTestId: null,
      unsubscribe: null,
    };
  },
  computed: {
    statKeys() {
      return [
        { key: "sent", label: "gesendet", cls: "" },
        { key: "smtpSuccess", label: "SMTP ok", cls: "mtp-stat--ok" },
        { key: "retry", label: "Wiederholt", cls: "" },
        { key: "smtpError", label: "SMTP-Fehler", cls: "mtp-stat--bad" },
        { key: "bounced", label: "Bounces", cls: "mtp-stat--warn" },
        { key: "bounceHard", label: "Adresse unbekannt", cls: "mtp-stat--bad" },
        { key: "fallbackSent", label: "→ Resend", cls: "" },
        { key: "fallbackSuccess", label: "Resend ok", cls: "mtp-stat--ok" },
        { key: "fallbackFailed", label: "endgültig", cls: "mtp-stat--bad" },
      ];
    },
    statToday() {
      const d = this.dispStats[new Date().toISOString().slice(0, 10)];
      return (d && d.counters) || {};
    },
    statWeek() {
      const since = Date.now() - 7 * 86400000;
      const out = {};
      Object.entries(this.dispStats).forEach(([day, d]) => {
        if (Date.parse(day) < since) return;
        Object.entries((d && d.counters) || {}).forEach(([k, v]) => (out[k] = (out[k] || 0) + Number(v || 0)));
      });
      return out;
    },
    storeRows() {
      const keys = Object.keys(this.dispDefaults).length ? Object.keys(this.dispDefaults) : Object.keys(this.disp.stores || {});
      return keys.sort().map((key) => {
        const def = this.dispDefaults[key] || {};
        const cur = (this.disp.stores || {})[key] || {};
        return { key, domain: def.domain || "", defaults: def, teamFrom: cur.teamFrom || "", teamReplyTo: cur.teamReplyTo || "", noreplyFrom: cur.noreplyFrom || "", noreplyReplyTo: cur.noreplyReplyTo || "", resendFrom: cur.resendFrom || "" };
      });
    },
    langItems() {
      return LANGS;
    },
    storeItems() {
      return STORES.map((s) => ({ value: s.key, text: s.text }));
    },
    selectedStore() {
      return STORES.find((s) => s.key === this.selectedStoreKey) || STORES[0];
    },
    // Karten: feste Reihenfolge aus TEMPLATE_META, Rest alphabetisch; Vollständigkeit über alle Sprachen
    templateList() {
      const ids = Object.keys(this.templates).sort((a, b) => {
        const ia = TEMPLATE_ORDER.indexOf(a);
        const ib = TEMPLATE_ORDER.indexOf(b);
        if (ia !== -1 || ib !== -1) return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        return a.localeCompare(b);
      });
      return ids.map((id) => {
        const tpl = this.templates[id];
        const keys = Object.keys(tpl.texts?.de || {});
        const missingLangs = LANGS.filter(({ value }) => keys.some((k) => !String(tpl.texts?.[value]?.[k] || "").trim())).length;
        return { id, ...this.meta(id), missingLangs, updatedAt: tpl.updatedAt || null, raw: tpl };
      });
    },
    template() {
      return this.selectedTemplateId ? this.templates[this.selectedTemplateId] || null : null;
    },
    deTexts() {
      return this.template?.texts?.de || {};
    },
    textKeys() {
      // Reihenfolge: subject zuerst, dann wie im deutschen Original
      const keys = Object.keys(this.deTexts);
      return ["subject", ...keys.filter((k) => k !== "subject")].filter((k) => keys.includes(k));
    },
    savedLangTexts() {
      return this.template?.texts?.[this.selectedLang] || {};
    },
    isDirty() {
      return this.textKeys.some((k) => (this.draft[k] || "") !== (this.savedLangTexts[k] || ""));
    },
    // Fehlende/leere Keys je Sprache (gegen das deutsche Key-Set)
    completeness() {
      const out = {};
      const keys = Object.keys(this.deTexts);
      for (const { value: lang } of LANGS) {
        const t = this.template?.texts?.[lang] || {};
        out[lang] = keys.filter((k) => !String(t[k] || "").trim()).length;
      }
      return out;
    },
    previewRendered() {
      return renderMail(this.template, { ...this.savedLangTexts, ...this.draft }, this.selectedStore, this.selectedLang);
    },
    previewHtml() {
      return this.previewRendered.html;
    },
    previewSubject() {
      return this.previewRendered.subject;
    },
  },
  watch: {
    // Store-Wechsel springt auf dessen Standardsprache — das ist die Sicht,
    // die die meisten Kunden dieses Stores bekommen
    selectedStoreKey() {
      if (this.view === "edit") this.switchLang(this.selectedStore.defaultLang);
    },
    savedLangTexts() {
      // Fremd-Update (anderer Tab/Seed) nur übernehmen, wenn lokal nichts editiert ist
      if (!this.isDirty) this.resetDraft();
    },
  },
  created() {
    this.unsubDisp = onSnapshot(doc(db, "settings", "mailDispatcher"), (snap) => {
      const d = snap.exists() ? snap.data() : {};
      this.disp = { enabled: d.enabled === true, redirectAllTo: d.redirectAllTo || "", stores: d.stores || {}, bounceMailbox: d.bounceMailbox || null };
    });
    this.unsubDispRecent = onSnapshot(query(collection(db, "mailDispatcher"), orderBy("createdAt", "desc"), limit(40)), (snap) => {
      this.dispRecent = snap.docs.map((x) => ({ id: x.id, ...x.data() }));
    });
    this.unsubDispStats = onSnapshot(query(collection(db, "mailStats"), orderBy("day", "desc"), limit(8)), (snap) => {
      const m = {};
      snap.docs.forEach((x) => (m[x.id] = x.data()));
      this.dispStats = m;
    });
    this.loadDispDefaults();
    this.unsubAddrMail = onSnapshot(doc(db, "settings", "addressCheck"), (snap) => {
      const d = snap.exists() ? snap.data() : {};
      this.addrMail = { mail: d.mail === true, mailSince: d.mailSince || null, mailDelayHours: d.mailDelayHours ?? 1, mailReminderHours: d.mailReminderHours ?? 48, mailMaxAgeDays: d.mailMaxAgeDays ?? 14 };
    });
    this.unsubscribe = onSnapshot(collection(db, "transactionalMailTemplates"), (snap) => {
      const next = {};
      snap.forEach((d) => {
        next[d.id] = d.data();
      });
      this.templates = next;
      this.loading = false;
    });
  },
  beforeDestroy() {
    if (this.unsubAddrMail) this.unsubAddrMail();
    if (this.unsubDisp) this.unsubDisp();
    if (this.unsubDispRecent) this.unsubDispRecent();
    if (this.unsubDispStats) this.unsubDispStats();
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    /* ---------- Mail-Dispatcher ---------- */
    async loadDispDefaults() {
      try {
        const call = httpsCallable(fbFunctions, "mailDispatcherAction");
        const res = await call({ action: "defaults" });
        if (res.data && res.data.success) {
          this.dispDefaults = res.data.stores || {};
          this.dispCallbackUrl = res.data.callbackUrl || "";
        }
      } catch (e) {
        console.warn("Dispatcher-Defaults:", e.message);
      }
    },
    async saveDisp() {
      this.dispSaving = true;
      try {
        await setDoc(doc(db, "settings", "mailDispatcher"), { enabled: !!this.disp.enabled, redirectAllTo: String(this.disp.redirectAllTo || "").trim(), ...(this.disp.enabled ? { enabledAt: Date.now() } : {}) }, { merge: true });
        EventBus.$emit(events.SNACKBAR, { message: this.disp.enabled ? "Dispatcher aktiv — liegengebliebene Mails werden beim nächsten Nachlauf gesendet" : "Dispatcher-Einstellungen gespeichert", type: "success" });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Speichern fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.dispSaving = false;
      }
    },
    async saveStores() {
      const stores = {};
      this.storeRows.forEach((r) => {
        const entry = {};
        ["teamFrom", "teamReplyTo", "noreplyFrom", "noreplyReplyTo", "resendFrom"].forEach((f) => { if (String(r[f] || "").trim()) entry[f] = String(r[f]).trim(); });
        if (Object.keys(entry).length) stores[r.key] = entry;
      });
      try {
        await setDoc(doc(db, "settings", "mailDispatcher"), { stores }, { merge: true });
        EventBus.$emit(events.SNACKBAR, { message: "Absender gespeichert", type: "success" });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Speichern fehlgeschlagen: ${e.message}`, type: "error" });
      }
    },
    async dispAction(action, extra = {}) {
      this.dispBusy = action === "pollNow" ? "poll" : action === "sweepNow" ? "sweep" : action;
      try {
        const call = httpsCallable(fbFunctions, "mailDispatcherAction");
        const res = await call({ action, ...extra });
        if (!res.data || !res.data.success) throw new Error((res.data && res.data.error) || "unbekannt");
        const d = res.data;
        const msg = action === "pollNow" ? (d.connected ? `Postfach geprüft: ${d.scanned} neue Bounce-Mails, ${d.matched} zugeordnet (${d.hard} unbekannt, ${d.soft} geblockt, ${d.fallback} → Resend)` : "Kein Postfach verbunden")
          : action === "sweepNow" ? (d.enabled ? `Nachlauf: ${d.processed} Mails bearbeitet ${JSON.stringify(d.results || {})}` : "Dispatcher ist aus — nichts gesendet")
          : action === "retry" ? `Erneut versucht: ${d.state}` : "OK";
        EventBus.$emit(events.SNACKBAR, { message: msg, type: "success" });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `${action} fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.dispBusy = "";
      }
    },
    async dispTestSend() {
      const to = (auth.currentUser && auth.currentUser.email) || "";
      if (!to) return EventBus.$emit(events.SNACKBAR, { message: "Keine eigene E-Mail am Konto", type: "error" });
      this.dispBusy = "test";
      try {
        const call = httpsCallable(fbFunctions, "mailDispatcherAction");
        const res = await call({ action: "testSend", to, shopId: this.selectedStore.shopId, countryCode: this.selectedStore.countryCode, kind: "noreply" });
        if (!res.data || !res.data.success) throw new Error((res.data && res.data.error) || "unbekannt");
        EventBus.$emit(events.SNACKBAR, { message: res.data.state === "DISABLED" ? `Testmail eingereiht (${res.data.id}) — Dispatcher ist aus, sie wartet` : `Testmail an ${to}: ${res.data.state}`, type: res.data.state === "SUCCESS" ? "success" : "warning" });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Testmail fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.dispBusy = "";
      }
    },
    async dispTestFallback() {
      const to = (auth.currentUser && auth.currentUser.email) || "";
      if (!to) return EventBus.$emit(events.SNACKBAR, { message: "Keine eigene E-Mail am Konto", type: "error" });
      this.dispBusy = "fallback";
      try {
        const call = httpsCallable(fbFunctions, "mailDispatcherAction");
        const res = await call({ action: "testFallback", to, shopId: this.selectedStore.shopId, countryCode: this.selectedStore.countryCode });
        if (!res.data || !res.data.success) throw new Error((res.data && res.data.error) || "unbekannt");
        EventBus.$emit(events.SNACKBAR, { message: res.data.ok ? `Rückfall-Test: SMTP-Mail und Resend-Mail an ${to} unterwegs — Status in der Liste` : `Rückfall nicht möglich: ${res.data.reason}`, type: res.data.ok ? "success" : "warning" });
        this.dispShowLog = true;
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Rückfall-Test fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.dispBusy = "";
      }
    },
    async dispConnectMailbox() {
      this.dispBusy = "oauth";
      try {
        const call = httpsCallable(fbFunctions, "mailDispatcherAction");
        const res = await call({ action: "oauthUrl" });
        if (!res.data || !res.data.success) throw new Error((res.data && res.data.error) || "unbekannt");
        this.dispCallbackUrl = res.data.callbackUrl || this.dispCallbackUrl;
        window.open(res.data.url, "_blank", "noopener,width=520,height=700");
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Verbinden fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.dispBusy = "";
      }
    },
    stateLabel(m) {
      const d = m.delivery || {};
      const map = { PENDING: "wartet", PROCESSING: "sendet …", SUCCESS: "SMTP ok", RETRY: `Wiederholung ${d.attempts || ""}`, ERROR: "Fehler", BOUNCED: d.bounce && d.bounce.kind === "hard" ? "Adresse unbekannt" : "geblockt", FALLBACK_SENT: "→ Resend", FALLBACK_SUCCESS: "Resend ok", FAILED: "endgültig" };
      return map[d.state] || d.state || "?";
    },
    stateClass(m) {
      const st = (m.delivery || {}).state;
      if (["SUCCESS", "FALLBACK_SUCCESS"].includes(st)) return "ok";
      if (["ERROR", "FAILED", "BOUNCED"].includes(st)) return "bad";
      if (["RETRY", "FALLBACK_SENT"].includes(st)) return "warn";
      return "neutral";
    },
    maskMail(to) {
      const s = Array.isArray(to) ? to.join(", ") : String(to || "");
      return s.replace(/^([^@]{2})[^@]*@/, "$1…@");
    },
    relTime(ms) {
      const t = typeof ms === "number" ? ms : Date.parse(ms) || 0;
      if (!t) return "";
      const diff = Math.round((Date.now() - t) / 60000);
      if (diff < 1) return "gerade eben";
      if (diff < 60) return `vor ${diff} min`;
      if (diff < 48 * 60) return `vor ${Math.round(diff / 60)} h`;
      return new Date(t).toLocaleDateString("de-DE");
    },
    /** Schalter/Zeiten der Adress-Check-Mails speichern; beim Einschalten gilt „ab jetzt" (mailSince), damit keine Altbestellungen angeschrieben werden */
    async saveAddrMail() {
      this.addrMailSaving = true;
      try {
        const patch = {
          mail: !!this.addrMail.mail,
          mailDelayHours: Number(this.addrMail.mailDelayHours) >= 0 ? Number(this.addrMail.mailDelayHours) : 1,
          mailReminderHours: Number(this.addrMail.mailReminderHours) > 0 ? Number(this.addrMail.mailReminderHours) : 48,
          mailMaxAgeDays: Number(this.addrMail.mailMaxAgeDays) > 0 ? Number(this.addrMail.mailMaxAgeDays) : 14,
        };
        if (patch.mail && !this.addrMail.mailSince) patch.mailSince = Date.now();
        await setDoc(doc(db, "settings", "addressCheck"), patch, { merge: true });
        EventBus.$emit(events.SNACKBAR, { message: patch.mail ? "Adress-Check-Mails aktiv — nur für Bestellungen ab jetzt" : "Adress-Check-Mails gespeichert", type: "success" });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Speichern fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.addrMailSaving = false;
      }
    },
    async addrMailDryRun() {
      this.addrMailDryRunning = true;
      try {
        const call = httpsCallable(fbFunctions, "addressValidationAction");
        const res = await call({ action: "mailDryRun" });
        if (!res.data || !res.data.success) throw new Error((res.data && res.data.error) || "unbekannt");
        this.addrMailDry = res.data;
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Trockenlauf fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.addrMailDryRunning = false;
      }
    },
    meta(id) {
      const m = TEMPLATE_META[id] || {};
      const tpl = this.templates[id] || {};
      return {
        name: m.name || tpl.name || id,
        description: m.description || tpl.description || "",
        icon: m.icon || "mdi-email-outline",
      };
    },
    langLabel(code) {
      return LANG_NAMES[code] || code;
    },
    shortDate(iso) {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
    },
    // Kartenvorschau: Texte in der Standardsprache des gewählten Stores (Fallback Deutsch)
    cardHtml(tpl) {
      const lang = tpl.raw.texts?.[this.selectedStore.defaultLang] ? this.selectedStore.defaultLang : "de";
      return renderMail(tpl.raw, tpl.raw.texts?.[lang] || {}, this.selectedStore, lang).html;
    },
    openEditor(id) {
      this.selectedTemplateId = id;
      this.selectedLang = this.templates[id]?.texts?.[this.selectedStore.defaultLang] ? this.selectedStore.defaultLang : "de";
      this.resetDraft();
      this.view = "edit";
    },
    backToGrid() {
      if (this.isDirty && !confirm("Ungespeicherte Änderungen verwerfen?")) return;
      this.view = "grid";
      this.selectedTemplateId = null;
      this.draft = {};
    },
    switchLang(lang) {
      if (lang === this.selectedLang) return;
      if (this.isDirty && !confirm("Ungespeicherte Änderungen verwerfen?")) return;
      this.selectedLang = lang;
      this.resetDraft();
    },
    rowsFor(key) {
      return String(this.deTexts[key] || "").length > 90 ? 3 : 1;
    },
    resetDraft() {
      const d = {};
      for (const k of this.textKeys) d[k] = this.savedLangTexts[k] || "";
      this.draft = d;
    },
    placeholderMismatch(key) {
      if (this.selectedLang === "de") return false;
      return extractPlaceholders(this.draft[key]) !== extractPlaceholders(this.deTexts[key]);
    },
    async saveLang() {
      this.saving = true;
      try {
        const clean = {};
        for (const k of this.textKeys) clean[k] = this.draft[k] || "";
        await updateDoc(doc(db, "transactionalMailTemplates", this.selectedTemplateId), {
          [`texts.${this.selectedLang}`]: clean,
          updatedAt: new Date().toISOString(),
        });
        EventBus.$emit(events.SNACKBAR, {
          message: `${this.langLabel(this.selectedLang)} gespeichert`,
          type: "success",
        });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Speichern fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.saving = false;
      }
    },
    // Aus der Karte: Standardsprache des Stores; im Editor: gewählte Sprache (ungespeichertes wird vorher gespeichert)
    async sendTestMail(templateId) {
      this.sendingTestId = templateId;
      try {
        const inEditor = this.view === "edit" && templateId === this.selectedTemplateId;
        if (inEditor && this.isDirty) {
          await this.saveLang();
        }
        const lang = inEditor ? this.selectedLang : this.selectedStore.defaultLang;
        const call = httpsCallable(fbFunctions, "sendTransactionalTestMail");
        const res = await call({
          templateId,
          lang,
          shopId: this.selectedStore.shopId,
          countryCode: this.selectedStore.countryCode,
          // Mit Order-Referenz kommen Sprache/Store/Kunde/Bilder aus der
          // echten Bestellung — Sprach-/Store-Auswahl oben wird dann ignoriert
          orderRef: inEditor ? (this.testOrderRef || "").trim() || undefined : undefined,
          via: this.testViaDispatcher ? "dispatcher" : undefined,
        });
        if (res?.data?.status === "ok") {
          const src = res.data.order ? `Bestellung ${res.data.order}` : res.data.store;
          EventBus.$emit(events.SNACKBAR, { message: `Testmail ${src} (${res.data.lang}) an ${res.data.email} gesendet`, type: "success" });
        } else {
          EventBus.$emit(events.SNACKBAR, { message: `Testversand fehlgeschlagen: ${res?.data?.message}`, type: "error" });
        }
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Testversand fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.sendingTestId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$accent: #7b5cd6;

.mtp {
  padding: 4px 2px 24px;
  color: #1d1d28;
}

/* ── Toolbar ── */
.mtp-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  &__title {
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }
  &__sub {
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(123, 92, 214, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    .v-icon { color: $accent; }
  }
}
.mtp-back {
  margin-left: -6px;
}
.mtp-select {
  max-width: 280px;

  &--store {
    max-width: 210px;
  }
  &--order {
    max-width: 185px;
  }
  &--store,
  &--order {
    ::v-deep .v-input__icon--prepend-inner .v-icon {
      color: #7b5cd6;
      font-size: 18px;
    }
  }
  ::v-deep .v-input__slot {
    background: #fff !important;
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(20, 20, 40, 0.08) !important;
    fieldset { border-color: transparent; }
  }
}
.mtp-btn {
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0.1px;
}
.mtp-empty {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  padding: 24px 4px;
  code {
    background: #edeafa;
    color: #6a4fc4;
    border-radius: 6px;
    padding: 1px 6px;
  }
}

/* ── Galerie (Vorbild: E-Mail-Marketing → Templates) ── */
.mtp-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}
.mtp-tplcard {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(20, 20, 40, 0.07);
  transition: box-shadow 0.15s, border-color 0.15s;

  &:hover {
    box-shadow: 0 4px 16px rgba(20, 20, 40, 0.12);
    .mtp-tplcard__hover { opacity: 1; }
  }
  &--incomplete {
    border-color: rgba(239, 83, 80, 0.35);
  }

  &__preview {
    position: relative;
    height: 250px;
    overflow: hidden;
    background: #f3f2f7;
    cursor: pointer;
  }
  // 600 px breite Mail, auf Kartenbreite skaliert und zentriert; keine Interaktion mit dem Inhalt
  &__frame {
    position: absolute;
    left: 50%;
    top: 0;
    width: 600px;
    height: 620px;
    border: 0;
    transform: translateX(-50%) scale(0.46);
    transform-origin: top center;
    pointer-events: none;
    background: #fff;
  }
  &__hover {
    position: absolute;
    inset: 0;
    background: rgba(30, 30, 42, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s;
  }
  &__meta {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    flex: 1;
  }
  &__icon {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background: rgba(123, 92, 214, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    .v-icon { color: $accent; }
  }
  &__text {
    flex: 1;
    min-width: 0;
  }
  &__name {
    font-size: 13.5px;
    font-weight: 700;
    line-height: 1.3;
  }
  &__desc {
    font-size: 12px;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.55);
    margin-top: 2px;
  }
  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
  }
  &__actions {
    display: flex;
    align-items: center;
    padding: 2px 6px 6px;
    .v-btn { text-transform: none; letter-spacing: 0; }
  }
}
.mtp-badge {
  font-size: 10.5px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.55);
  background: rgba(0, 0, 0, 0.05);
  border-radius: 999px;
  padding: 1px 8px;
  white-space: nowrap;

  &--ok {
    color: #2e7d32;
    background: #e8f5e9;
  }
  &--warn {
    color: #c62828;
    background: #fdecea;
  }
}

/* ── Sprach-Pills ── */
.mtp-langs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.mtp-lang {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  outline: none;
  cursor: pointer;
  background: #fff;
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.62);
  box-shadow: 0 1px 4px rgba(20, 20, 40, 0.08);
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(20, 20, 40, 0.13);
  }
  &--active {
    background: $accent;
    color: #fff;
    box-shadow: 0 4px 12px rgba(123, 92, 214, 0.35);
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;

    &--ok { background: #4caf50; }
    &--missing { background: #ef5350; }
  }
  &--active &__dot--ok { background: #b5f0b8; }
  &--active &__dot--missing { background: #ffc4c1; }

  &__count {
    background: #fdecea;
    color: #c62828;
    border-radius: 999px;
    font-size: 10.5px;
    padding: 1px 6px;
  }
  &--active &__count {
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
  }
}

/* ── Layout: Editor + Vorschau ── */
.mtp-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
}
.mtp-card {
  padding: 18px 20px;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    min-height: 28px;
  }
  &__title {
    font-size: 14.5px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
  }
  &__icon {
    color: $accent !important;
  }
}
.mtp-muted {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-left: auto;
}

/* ── Key-Editor ── */
.mtp-key {
  margin-bottom: 14px;

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    code {
      background: #edeafa;
      color: #6a4fc4;
      font-size: 11px;
      font-weight: 700;
      border-radius: 6px;
      padding: 2px 8px;
    }
  }
  &__input {
    ::v-deep .v-input__slot {
      border-radius: 10px;
      background: #fff !important;
      fieldset { border-color: rgba(0, 0, 0, 0.12); }
    }
    ::v-deep textarea {
      font-size: 13.5px;
      line-height: 1.5;
    }
    &--empty ::v-deep .v-input__slot {
      background: #fdecea !important;
      fieldset { border-color: #ef9a9a; }
    }
  }
  &__reference {
    font-size: 12px;
    line-height: 1.5;
    color: rgba(0, 0, 0, 0.42);
    margin-top: 4px;
    padding-left: 2px;
    white-space: pre-wrap;
  }
}
.mtp-warn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fff3e0;
  color: #b26a00;
  font-size: 10.5px;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 9px;
}

/* ── Vorschau ── */
.mtp-card--preview {
  position: sticky;
  top: 16px;
  align-self: start;
}
.mtp-subject {
  margin-left: auto;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #edeafa;
  color: #6a4fc4;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 12px;
}
.mtp-preview {
  width: 100%;
  height: 76vh;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #fff;
}

/* Adress-Check-Mails: Schalterkarte über der Galerie */
.mtp-switchcard { border: 1px solid rgba(0, 0, 0, 0.08); border-radius: 14px; padding: 14px 16px; margin-bottom: 18px; background: #fff; display: grid; gap: 10px; }
.mtp-switchcard--on { border-color: rgba(46, 125, 50, 0.35); background: rgba(46, 125, 50, 0.04); }
.mtp-switchcard__head { display: flex; align-items: center; gap: 12px; }
.mtp-switchcard__title { font-weight: 700; font-size: 14px; }
.mtp-switchcard__sub { font-size: 12px; color: rgba(0, 0, 0, 0.55); max-width: 720px; }
.mtp-switchcard__row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.mtp-switchcard__num { max-width: 170px; }
.mtp-switchcard__result { font-size: 12px; display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.mtp-switchcard__skipped { color: rgba(0, 0, 0, 0.45); }

/* Mail-Dispatcher */
.mtp-dispatch code { font-size: 11px; background: rgba(0, 0, 0, 0.05); padding: 1px 4px; border-radius: 4px; }
.mtp-dispatch__stats { display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-end; }
.mtp-stat { min-width: 92px; padding: 8px 10px; border-radius: 10px; background: rgba(0, 0, 0, 0.04); }
.mtp-stat--ok { background: rgba(46, 125, 50, 0.08); }
.mtp-stat--warn { background: rgba(240, 169, 46, 0.14); }
.mtp-stat--bad { background: rgba(198, 40, 40, 0.08); }
.mtp-stat__val { font-weight: 800; font-size: 18px; line-height: 1.1; }
.mtp-stat__week { font-weight: 500; font-size: 12px; color: rgba(0, 0, 0, 0.5); margin-left: 4px; }
.mtp-stat__label { font-size: 11px; color: rgba(0, 0, 0, 0.6); margin-top: 2px; }
.mtp-stat__hint { font-size: 11px; color: rgba(0, 0, 0, 0.45); padding-bottom: 8px; }
.mtp-dispatch__redirect { max-width: 360px; }
.mtp-dispatch__mailbox { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 13px; padding: 8px 10px; border-radius: 10px; background: rgba(0, 0, 0, 0.03); }
.mtp-dispatch__callback { font-size: 11px; color: rgba(0, 0, 0, 0.55); }
.mtp-dispatch__stores { display: grid; gap: 8px; }
.mtp-dispatch__storesHead { font-size: 12px; color: rgba(0, 0, 0, 0.6); }
.mtp-dispatch__storeRow { display: grid; grid-template-columns: 120px repeat(5, minmax(140px, 1fr)); gap: 8px; align-items: center; }
.mtp-dispatch__storeName { display: grid; font-size: 12px; span { color: rgba(0, 0, 0, 0.5); } }
.mtp-dispatch__log { display: grid; gap: 2px; font-size: 12px; max-height: 420px; overflow: auto; }
.mtp-dispatch__logRow { display: grid; grid-template-columns: 120px 90px 180px 1fr 110px 60px; gap: 8px; align-items: center; padding: 4px 8px; border-radius: 6px; background: rgba(0, 0, 0, 0.025); }
.mtp-dispatch__logRow--ok .mtp-dispatch__logState { color: #2e7d32; }
.mtp-dispatch__logRow--bad .mtp-dispatch__logState { color: #c62828; }
.mtp-dispatch__logRow--warn .mtp-dispatch__logState { color: #b26a00; }
.mtp-dispatch__logState { font-weight: 700; }
.mtp-dispatch__logWhen, .mtp-dispatch__logStore { color: rgba(0, 0, 0, 0.5); }
.mtp-dispatch__logSubject { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mtp-dispatch__empty { color: rgba(0, 0, 0, 0.5); padding: 6px 8px; }
.mtp-viaswitch { flex: 0 0 auto; }
@media (max-width: 1100px) { .mtp-dispatch__storeRow { grid-template-columns: 1fr 1fr; } .mtp-dispatch__logRow { grid-template-columns: 100px 80px 1fr 60px; .mtp-dispatch__logSubject, .mtp-dispatch__logStore { display: none; } } }
</style>
